import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { fmtMoney } from "../../lib/format";

/**
 * Phase 2D.3B.3 — „Представяне по екип".
 *
 * CURRENT STATE: the open workload and the live pipeline value of each team,
 * grouped by the authoritative ``quotations.team_id``. It never depends on the
 * selected period, and the „Без екип" row is always shown when it has rows —
 * unattributed work is exactly what an Owner needs to see.
 */
export default function DashboardByTeam({ rows, loading, onRowClick }) {
  const { t } = useTranslation();
  const displayRows = useMemo(() => rows || [], [rows]);
  const maxOpen = useMemo(
    () => Math.max(1, ...displayRows.map((r) => r.open_count || 0)),
    [displayRows],
  );
  const anyMixed = displayRows.some((r) => r.mixed_currency);

  if (loading) {
    return <div className="qw-skel h-280 stretch" aria-busy="true" />;
  }
  if (!displayRows.length) {
    return (
      <div className="qw-empty" data-testid="by-team-empty">
        {t("dashboard.by_team_empty")}
      </div>
    );
  }

  return (
    <div>
      <div
        className="qw-table-wrap"
        tabIndex={0}
        role="region"
        aria-label={t("dashboard.section_by_team")}
      >
        <table className="qw-table qw-by-team-table" data-testid="by-team-table">
          <thead>
            <tr>
              <th>{t("dashboard.by_team_column_team")}</th>
              <th style={{ textAlign: "right" }}>{t("dashboard.by_team_column_open")}</th>
              <th style={{ textAlign: "right" }}>{t("dashboard.by_team_column_active_value")}</th>
              <th style={{ textAlign: "right" }}>{t("dashboard.by_team_column_awaiting")}</th>
              <th style={{ textAlign: "right" }}>{t("dashboard.by_team_column_expiring")}</th>
            </tr>
          </thead>
          <tbody>
            {displayRows.map((r) => {
              const key = r.team_id || "no-team";
              const label = r.team_id
                ? `${r.team_name || "—"}${r.team_is_active === false ? t("dashboard.by_team_archived_suffix") : ""}`
                : t("dashboard.by_team_no_team");
              const width = Math.max(4, Math.round(((r.open_count || 0) / maxOpen) * 100));
              return (
                <tr
                  key={key}
                  data-clickable="true"
                  onClick={() => onRowClick(r.team_id)}
                  data-testid={`by-team-row-${key}`}
                >
                  <td>
                    <div className="qw-team-cell">
                      <span className={`qw-team-name${r.team_id ? "" : " no-team"}`}>{label}</span>
                      <span className="qw-mini-bar">
                        <span className="fill" style={{ width: `${width}%` }} />
                      </span>
                    </div>
                  </td>
                  <td className="num">{(r.open_count || 0).toLocaleString("bg-BG")}</td>
                  <td className="num">
                    {fmtMoney(r.active_value, "EUR")}
                    {r.mixed_currency && (
                      <span
                        className="qw-by-owner-mixed"
                        title={t("dashboard.mixed_currency_note")}
                        data-testid={`by-team-mixed-${key}`}
                      >
                        ⚠
                      </span>
                    )}
                  </td>
                  <td className="num">{(r.awaiting_count || 0).toLocaleString("bg-BG")}</td>
                  <td className="num">{(r.expiring_7_count || 0).toLocaleString("bg-BG")}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="qw-by-owner-disclaimer">
        {t("dashboard.by_team_note")}
        {anyMixed && (
          <span style={{ display: "block", color: "#b45309", marginTop: 4 }}>
            {t("dashboard.mixed_currency_note")}
          </span>
        )}
      </div>
    </div>
  );
}
